import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Loading from '../../../components/common/Loading';
import useNotify from '../../../hooks/useNotify';
import reolinkVideoApi, { resolveReolinkVideoId } from '../../../services/reolinkVideoApi';
import ReolinkVideoViewer from './ReolinkVideoViewer';
import {
  formatAlertsCameraFileLabel,
  formatFtpFileClock,
  formatReolinkRecordingClock,
  getCameraNumberFromFileName,
  getReolinkVideoSortMs,
} from '../utils/reolinkCameraBattery';
import { fieldHighlightFileMatchesBridge } from '../utils/reolinkBridgeMatch';
import './AlertsVideoPanel.css';

const PAGE_SIZE = 20;

const videoBaseName = (video) => {
  const raw = String(video?.fileName || video?.name || video?.s3Key || video?.key || '');
  if (!raw) return '';
  const parts = raw.split(/[/\\]/);
  return parts[parts.length - 1] || raw;
};

const fieldHighlightNameCandidates = (video) => {
  const names = [];
  [video?.fileName, video?.name, video?.s3Key, video?.key].forEach((raw) => {
    if (!raw) return;
    const base = String(raw).split(/[/\\]/).pop() || String(raw);
    if (base && !names.includes(base)) names.push(base);
  });
  return names;
};

/** Camera clock in the FTP name, as UTC so IST does not rank 7 Sep 00:05 behind 6 Sep 23:58. */
const parseFieldHighlightClockMs = (fileName) => {
  const match = String(fileName || '').match(/(\d{8})(\d{6})(?:\.[a-z0-9]+)?$/i);
  if (!match) return 0;
  const iso = `${match[1].slice(0, 4)}-${match[1].slice(4, 6)}-${match[1].slice(6, 8)}T${match[2].slice(0, 2)}:${match[2].slice(2, 4)}:${match[2].slice(4, 6)}Z`;
  const ms = Date.parse(iso);
  return Number.isFinite(ms) ? ms : 0;
};

const fieldHighlightClockFileName = (video) =>
  fieldHighlightNameCandidates(video).find((name) => formatFtpFileClock(name))
  || videoBaseName(video);

/**
 * "Parsonage 9-31 top_00_20260825033326.mp4"
 * → bridge words before the logger suffix, suffix "9-31"
 */
const parseFieldHighlightFileName = (fileName) => {
  const base = String(fileName || '').replace(/\.[a-z0-9]+$/i, '');
  const match = base.match(/^(.*?)[\s_]+(\d{1,2}-\d{2})(?![0-9])/i);
  if (!match || !String(match[1]).trim()) return null;
  return {
    bridgePart: match[1].trim(),
    loggerSuffix: String(match[2]).toLowerCase(),
  };
};

/** "00-00-09-31" → "09-31", "9-31" (filename uses the short form). */
const loggerSerialSuffixes = (serial) => {
  const parts = String(serial || '').split(/[-_]/).filter(Boolean);
  if (parts.length < 2) return [];
  const prev = parts[parts.length - 2];
  const last = parts[parts.length - 1];
  const suffixes = new Set();
  suffixes.add(`${prev}-${last}`.toLowerCase());
  if (/^\d+$/.test(prev)) suffixes.add(`${Number(prev)}-${last}`.toLowerCase());
  if (/^\d+$/.test(prev) && /^\d+$/.test(last)) {
    suffixes.add(`${Number(prev)}-${Number(last)}`.toLowerCase());
  }
  return [...suffixes];
};

/** "00-00-09-45" → "9-45" (display: last two serial segments, strip leading zeros on the first). */
const loggerSerialDisplaySuffix = (serial) => {
  const parts = String(serial || '').split(/[-_]/).filter(Boolean);
  if (parts.length < 2) return '';
  const prev = parts[parts.length - 2];
  const last = parts[parts.length - 1];
  const prevDisp = /^\d+$/.test(prev) ? String(Number(prev)) : prev;
  return `${prevDisp}-${last}`;
};

/** "Parsonage 9-31 top_00_..." / "… top-right …" → Top | Bottom | Center */
const parseFieldHighlightPosition = (fileName) => {
  const raw = String(fileName || '')
    .replace(/\.[a-z0-9]+$/i, '')
    .toLowerCase();
  const afterSuffix = raw.replace(/^.*?[\s_]+(\d{1,2}-\d{2})\b/i, ' ');
  const haystack = afterSuffix !== raw ? afterSuffix : ` ${raw} `;
  if (/(?:^|[\s_-])top(?:[-_\s]?(?:right|left))?(?:$|[\s_-])/.test(haystack)) return 'Top';
  if (/(?:^|[\s_-])bottom(?:[-_\s]?(?:right|left))?(?:$|[\s_-])/.test(haystack)) return 'Bottom';
  if (/(?:^|[\s_-])cent(?:er|re)(?:$|[\s_-])/.test(haystack)) return 'Center';
  return '';
};

/** File column: "Parsonage 9-31 - Top" from the filename's own logger suffix, then position. */
const formatFieldHighlightFileLabel = (fileName, bridgeName, serial) => {
  const suffix = parseFieldHighlightFileName(fileName)?.loggerSuffix
    || loggerSerialDisplaySuffix(serial)
    || '';
  const name = String(bridgeName || '').trim();
  const base = name && suffix
    ? `${name} ${suffix}`
    : name || formatAlertsCameraFileLabel(fileName, bridgeName);
  const position = parseFieldHighlightPosition(fileName);
  return position ? `${base} - ${position}` : base;
};

/** Prefer camera-clock timestamp in the filename so page 1 is the latest recording. */
const fieldHighlightSortMs = (video) => {
  const names = fieldHighlightNameCandidates(video);
  for (let i = 0; i < names.length; i += 1) {
    const ms = parseFieldHighlightClockMs(names[i]);
    if (ms) return ms;
  }
  return getReolinkVideoSortMs(video);
};

const videoMatchesBridgeName = (video, bridgeName) => {
  const names = fieldHighlightNameCandidates(video);
  const fileName = names.find(Boolean) || videoBaseName(video);
  return fieldHighlightFileMatchesBridge(fileName, bridgeName);
};

/** "00-00-09-40" → 40 (matches "Camera 40" / "40_00_timestamp"). */
const loggerSerialLastNumber = (serial) => {
  const parts = String(serial || '').split(/[-_]/).filter(Boolean);
  if (!parts.length) return null;
  const n = Number(parts[parts.length - 1]);
  return Number.isFinite(n) ? n : null;
};

const videoMatchesSelectedLogger = (video, selectedSerial) => {
  const names = fieldHighlightNameCandidates(video);
  const fileNames = names.length ? names : [videoBaseName(video)];
  const lastNum = loggerSerialLastNumber(selectedSerial);
  const allowedSuffixes = new Set(loggerSerialSuffixes(selectedSerial));

  return fileNames.some((fileName) => {
    const cameraNo = getCameraNumberFromFileName(fileName);
    if (lastNum != null && cameraNo === lastNum) return true;
    const parsed = parseFieldHighlightFileName(fileName);
    return Boolean(parsed && allowedSuffixes.has(parsed.loggerSuffix));
  });
};

/**
 * Field Highlights files: "{bridge} {logger-suffix} ...mp4" or "{bridge} {40}_00_{timestamp}.mp4"
 * The site name is the first word (after an Indus Informatics prefix). A later word
 * such as "east" in "Destin (East Pass)" does not attach the clip to another bridge.
 * Default (no logger): every clip whose site name is this bridge.
 * Selected logger 00-00-09-40 → only Camera 40 / 9-40 videos, not 39.
 * An empty 9-61 match stays empty — 9-62 files are not listed under 9-61.
 * The bridge-wide list is kept only when names have no logger suffix
 * (Theo Baars "1_00_...." camera files, whose device serial is not the camera number).
 */
const fileHasLoggerSuffix = (fileName) =>
  /(?:^|[\s_])\d{1,2}-\d{2}(?![0-9])/i.test(String(fileName || ''));

const videoHasLoggerSuffix = (video) =>
  fieldHighlightNameCandidates(video).some(fileHasLoggerSuffix);

const filterFieldHighlightVideos = (videos, bridge, selectedSerial) => {
  const list = Array.isArray(videos) ? videos : [];
  const bridgeName = bridge?.name || '';
  const forBridge = bridgeName
    ? list.filter((video) => videoMatchesBridgeName(video, bridgeName))
    : list;
  if (!selectedSerial) return forBridge;

  const matched = forBridge.filter((video) => videoMatchesSelectedLogger(video, selectedSerial));
  if (matched.length) return matched;
  if (forBridge.some(videoHasLoggerSuffix)) return [];
  return forBridge;
};

function VideosPagination({ page, totalPages, onPageChange }) {
  if (totalPages <= 1) return null;
  const currentPage = Math.min(Math.max(page, 1), totalPages);
  return (
    <div className="sensor-video-pagination">
      <button
        type="button"
        className="sensor-video-pagination-btn"
        disabled={currentPage <= 1}
        onClick={() => onPageChange(currentPage - 1)}
      >
        Previous
      </button>
      <span className="sensor-video-pagination-info">
        Page {currentPage} of {totalPages}
      </span>
      <button
        type="button"
        className="sensor-video-pagination-btn"
        disabled={currentPage >= totalPages}
        onClick={() => onPageChange(currentPage + 1)}
      >
        Next
      </button>
    </div>
  );
}

/**
 * Field Highlights — Reolink list + play for selected bridge.
 *
 * GET /api/bridges/{bridgeId}/reolink-videos?fileType=video
 * Play → GET /api/bridges/{bridgeId}/reolink-videos/{id}/url
 *
 * Never uses /api/cameras. Never opens S3. Date filter is not required.
 */
export default function AlertsVideoPanel({ bridge, dataLogger }) {
  const { showToast } = useNotify();
  const showToastRef = useRef(showToast);
  showToastRef.current = showToast;
  const bridgeId = bridge?._id || bridge?.id || null;
  const bridgeName = bridge?.name || '';
  const dataLoggerSerial = dataLogger?.serialNo || '';

  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [viewerVideo, setViewerVideo] = useState(null);

  const filteredVideos = useMemo(() => {
    const list = filterFieldHighlightVideos(videos, bridge, dataLoggerSerial);
    return [...list].sort((a, b) => fieldHighlightSortMs(b) - fieldHighlightSortMs(a));
  }, [videos, bridge, dataLoggerSerial]);

  const totalPages = Math.max(1, Math.ceil(filteredVideos.length / PAGE_SIZE) || 1);
  const currentPage = Math.min(page, totalPages);
  const pageItems = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filteredVideos.slice(start, start + PAGE_SIZE);
  }, [filteredVideos, currentPage]);

  const applyVideoList = (result) => {
    const list = Array.isArray(result?.videos) ? [...result.videos] : [];
    list.sort((a, b) => getReolinkVideoSortMs(b) - getReolinkVideoSortMs(a));
    setVideos(list);
    setPage(1);
  };

  const loadVideos = useCallback(async (force = false) => {
    if (!bridgeId) {
      setVideos([]);
      setError(null);
      return;
    }

    const cached = reolinkVideoApi.peekListByBridge(bridgeId);
    if (cached?.videos?.length) {
      applyVideoList(cached);
      setError(null);
      if (!force) {
        setLoading(false);
        return;
      }
    }

    setError(null);
    if (!cached?.videos?.length) setLoading(true);

    try {
      const result = await reolinkVideoApi.listByBridge(bridgeId, { force });
      applyVideoList(result);
    } catch (err) {
      if (err?.status === 404) {
        setVideos([]);
        setError(null);
        return;
      }
      const fallback = reolinkVideoApi.peekListByBridge(bridgeId);
      if (fallback?.videos?.length) {
        applyVideoList(fallback);
        setError(null);
        return;
      }
      setVideos([]);
      const status = err?.status;
      let msg = err?.message || 'Failed to load Reolink videos';
      if (status === 403) msg = 'No access to this bridge video.';
      if (/timeout/i.test(msg)) {
        msg = 'Field Highlights took too long to load. Retry.';
      }
      setError(msg);
      showToastRef.current(msg, 'error');
    } finally {
      setLoading(false);
    }
  }, [bridgeId]);

  useEffect(() => {
    setViewerVideo(null);
    setPage(1);
    loadVideos();
  }, [bridgeId, loadVideos]);

  useEffect(() => {
    setPage(1);
    setViewerVideo(null);
  }, [dataLoggerSerial]);

  if (!bridgeId) {
    return (
      <div className="alerts-video-empty">
        Select a bridge to view Reolink videos.
      </div>
    );
  }

  return (
    <div className="alerts-video-panel">
      <div className="alerts-video-recordings-title">Video</div>

      {loading && !viewerVideo && videos.length === 0 ? (
        <Loading message="Loading videos..." />
      ) : error && !viewerVideo && videos.length === 0 ? (
        <div className="alerts-video-empty">
          <p>{error}</p>
          <button type="button" className="btn btn-blue btn-sm" onClick={() => loadVideos(true)}>
            Retry
          </button>
        </div>
      ) : viewerVideo && bridgeId ? (
        <ReolinkVideoViewer
          bridgeId={bridgeId}
          video={viewerVideo}
          onClose={() => setViewerVideo(null)}
        />
      ) : (
        <>
          <div className="sensor-video-table-wrap alerts-video-table-wrap">
            <table className="alerts-video-table" style={{ fontSize: 11, width: '100%' }}>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Time</th>
                  <th>File</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {pageItems.map((video) => {
                  const vid = resolveReolinkVideoId(video);
                  const clockFileName = fieldHighlightClockFileName(video);
                  const { date, time } = formatReolinkRecordingClock({
                    ...video,
                    fileName: clockFileName,
                  });
                  const fileName = video.fileName || clockFileName || 'Video';
                  const fileLabel = formatFieldHighlightFileLabel(
                    fileName,
                    bridgeName,
                    dataLoggerSerial,
                  );
                  return (
                    <tr key={vid || fileName}>
                      <td>{date}</td>
                      <td>{time}</td>
                      <td className="alerts-video-filename" title={fileName}>
                        <span>{fileLabel}</span>
                      </td>
                      <td>
                        <button
                          type="button"
                          className="btn btn-blue btn-sm"
                          disabled={!vid}
                          onClick={() => setViewerVideo(video)}
                        >
                          Play
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {pageItems.length === 0 && (
                  <tr>
                    <td colSpan={4} className="sensor-video-panel-empty">
                      {dataLoggerSerial
                        ? 'No Reolink videos found for this data logger.'
                        : 'No Reolink videos found for this bridge.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <VideosPagination
            page={currentPage}
            totalPages={totalPages}
            onPageChange={setPage}
          />
        </>
      )}
    </div>
  );
}
